import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

const {
  FiChevronLeft, FiChevronRight, FiCalendar, FiClock,
  FiUser, FiMail, FiDollarSign, FiCheck, FiX,
  FiInfo, FiSun, FiMoon
} = FiIcons;

const STATUS_META = {
  confirmed:   { label: 'Confirmed',   color: 'bg-green-500',  bg: 'bg-green-50',  border: 'border-green-200',  text: 'text-green-700' },
  pending_merchant: { label: 'Pending', color: 'bg-amber-500',  bg: 'bg-amber-50',  border: 'border-amber-200',  text: 'text-amber-700' },
  pending_admin:    { label: 'Pending', color: 'bg-amber-500',  bg: 'bg-amber-50',  border: 'border-amber-200',  text: 'text-amber-700' },
  pending:          { label: 'Pending', color: 'bg-amber-500',  bg: 'bg-amber-50',  border: 'border-amber-200',  text: 'text-amber-700' },
  completed:  { label: 'Completed',   color: 'bg-purple-500', bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700' },
  cancelled:  { label: 'Cancelled',   color: 'bg-gray-400',   bg: 'bg-gray-50',   border: 'border-gray-200',   text: 'text-gray-500' },
  rejected:   { label: 'Rejected',    color: 'bg-red-400',    bg: 'bg-red-50',    border: 'border-red-200',    text: 'text-red-700' },
};

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
];

const WEEKDAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

const BookingCalendar = ({ bookings = [], onUpdateStatus, loading = false, getListingTitle }) => {
  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [selectedDate, setSelectedDate] = useState(null);

  const bookingsByDate = useMemo(() => {
    const map = {};
    bookings.forEach(b => {
      const d = b.start_time ? new Date(b.start_time) : null;
      if (!d || isNaN(d)) return;
      const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
      if (!map[key]) map[key] = [];
      map[key].push(b);
    });
    return map;
  }, [bookings]);

  const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();
  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const daysInPrevMonth = new Date(currentYear, currentMonth, 0).getDate();

  const prevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(y => y - 1);
    } else {
      setCurrentMonth(m => m - 1);
    }
    setSelectedDate(null);
  };

  const nextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(y => y + 1);
    } else {
      setCurrentMonth(m => m + 1);
    }
    setSelectedDate(null);
  };

  const goToToday = () => {
    setCurrentMonth(today.getMonth());
    setCurrentYear(today.getFullYear());
    const key = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;
    setSelectedDate(key);
  };

  const isToday = (day) => {
    return day === today.getDate() && currentMonth === today.getMonth() && currentYear === today.getFullYear();
  };

  const selectedBookings = selectedDate ? (bookingsByDate[selectedDate] || []) : [];

  // Build calendar grid: [prevDays..., currentDays..., nextDays...]
  const calendarDays = [];
  // prev month trailing days
  for (let i = firstDayOfMonth - 1; i >= 0; i--) {
    calendarDays.push({ day: daysInPrevMonth - i, type: 'prev' });
  }
  // current month days
  for (let d = 1; d <= daysInMonth; d++) {
    calendarDays.push({ day: d, type: 'current' });
  }
  // next month leading days to fill 6 rows (42 cells)
  const remaining = 42 - calendarDays.length;
  for (let d = 1; d <= remaining; d++) {
    calendarDays.push({ day: d, type: 'next' });
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-[32px] border border-brand-dark p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-accent-navy rounded-2xl flex items-center justify-center text-white">
            <SafeIcon icon={FiCalendar} className="w-6 h-6" />
          </div>
          <div>
            <h3 className="text-xl font-serif text-accent-navy">{MONTHS[currentMonth]} {currentYear}</h3>
            <p className="text-[10px] font-black uppercase tracking-widest text-text-muted">
              {bookings.length} total booking{bookings.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={goToToday} className="px-4 py-2.5 bg-brand-light border border-brand-dark rounded-xl text-[10px] font-black uppercase tracking-widest text-text-muted hover:border-accent-gold hover:text-accent-navy transition-all">
            Today
          </button>
          <button onClick={prevMonth} className="p-2.5 bg-brand-light border border-brand-dark rounded-xl text-text-muted hover:border-accent-gold hover:text-accent-navy transition-all">
            <SafeIcon icon={FiChevronLeft} className="w-5 h-5" />
          </button>
          <button onClick={nextMonth} className="p-2.5 bg-brand-light border border-brand-dark rounded-xl text-text-muted hover:border-accent-gold hover:text-accent-navy transition-all">
            <SafeIcon icon={FiChevronRight} className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar Grid */}
        <div className="lg:col-span-2 bg-white rounded-[32px] border border-brand-dark p-6">
          {/* Weekday headers */}
          <div className="grid grid-cols-7 mb-3">
            {WEEKDAYS.map(wd => (
              <div key={wd} className="text-center py-2 text-[10px] font-black uppercase tracking-widest text-text-muted">
                {wd}
              </div>
            ))}
          </div>

          {/* Days */}
          <div className="grid grid-cols-7 gap-1">
            {calendarDays.map((cell, idx) => {
              const dateKey = cell.type === 'current'
                ? `${currentYear}-${String(currentMonth+1).padStart(2,'0')}-${String(cell.day).padStart(2,'0')}`
                : null;
              const dayBookings = dateKey ? (bookingsByDate[dateKey] || []) : [];
              const hasBookings = dayBookings.length > 0;
              const isSelected = selectedDate === dateKey;
              const todayClass = cell.type === 'current' && isToday(cell.day)
                ? 'ring-2 ring-accent-gold ring-offset-2'
                : '';

              return (
                <button
                  key={idx}
                  onClick={() => cell.type === 'current' && setSelectedDate(dateKey)}
                  disabled={cell.type !== 'current'}
                  className={`
                    relative aspect-square rounded-2xl p-1.5 flex flex-col items-center justify-start transition-all
                    ${cell.type === 'current'
                      ? `hover:bg-brand-light cursor-pointer ${isSelected ? 'bg-brand-light border-2 border-accent-gold' : 'border border-transparent'} ${todayClass}`
                      : 'opacity-30 cursor-default'
                    }
                  `}
                >
                  <span className={`
                    text-sm font-bold w-7 h-7 flex items-center justify-center rounded-full
                    ${cell.type === 'current' && isToday(cell.day) ? 'bg-accent-gold text-white' : 'text-accent-navy'}
                  `}>
                    {cell.day}
                  </span>

                  {hasBookings && (
                    <div className="mt-auto flex flex-wrap justify-center gap-0.5 w-full px-0.5">
                      {dayBookings.slice(0, 4).map((b, i) => {
                        const meta = STATUS_META[b.status] || STATUS_META.pending;
                        return (
                          <div key={i} className={`w-1.5 h-1.5 rounded-full ${meta.color}`} />
                        );
                      })}
                      {dayBookings.length > 4 && (
                        <span className="text-[8px] font-black text-text-muted leading-none">+</span>
                      )}
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Legend */}
          <div className="mt-5 pt-4 border-t border-brand-light flex flex-wrap gap-3">
            {Object.entries(STATUS_META).filter(([k]) => ['confirmed','pending_merchant','completed','cancelled'].includes(k)).map(([key, meta]) => (
              <div key={key} className="flex items-center gap-1.5">
                <div className={`w-2.5 h-2.5 rounded-full ${meta.color}`} />
                <span className="text-[10px] font-bold text-text-muted">{meta.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Selected Day Detail Panel */}
        <div className="bg-white rounded-[32px] border border-brand-dark p-6 flex flex-col min-h-[400px]">
          <div className="flex items-center justify-between mb-5 pb-4 border-b border-brand-light">
            <div>
              <h4 className="font-serif text-lg text-accent-navy">
                {selectedDate
                  ? new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
                  : 'Select a Date'}
              </h4>
              <p className="text-[10px] font-black uppercase tracking-widest text-text-muted mt-0.5">
                {selectedBookings.length} booking{selectedBookings.length !== 1 ? 's' : ''}
              </p>
            </div>
            {selectedDate && (
              <button onClick={() => setSelectedDate(null)} className="p-2 hover:bg-brand-light rounded-xl text-text-muted transition-all">
                <SafeIcon icon={FiX} className="w-4 h-4" />
              </button>
            )}
          </div>

          <div className="flex-1 overflow-y-auto custom-scrollbar space-y-3">
            <AnimatePresence>
              {selectedBookings.length > 0 ? (
                selectedBookings.map(booking => {
                  const meta = STATUS_META[booking.status] || STATUS_META.pending;
                  const timeStr = booking.start_time
                    ? new Date(booking.start_time).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
                    : '—';
                  return (
                    <motion.div
                      key={booking.id}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      className={`p-4 rounded-2xl border ${meta.border} ${meta.bg} space-y-2.5`}
                    >
                      <div className="flex items-center justify-between">
                        <span className={`text-[9px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full ${meta.bg} ${meta.text} border ${meta.border}`}>
                          {meta.label}
                        </span>
                        <span className="text-[10px] font-bold text-text-muted flex items-center gap-1">
                          <SafeIcon icon={FiClock} className="w-3 h-3" /> {timeStr}
                        </span>
                      </div>

                      <p className="font-bold text-accent-navy text-sm">
                        {booking.listing_title || getListingTitle?.(booking.listing_id) || 'Service Booking'}
                      </p>

                      <div className="flex items-center gap-2 text-xs text-text-muted">
                        <SafeIcon icon={FiUser} className="w-3.5 h-3.5" />
                        <span>{booking.customer_name || '—'}</span>
                      </div>
                      {booking.customer_email && (
                        <div className="flex items-center gap-2 text-xs text-text-muted">
                          <SafeIcon icon={FiMail} className="w-3.5 h-3.5" />
                          <span>{booking.customer_email}</span>
                        </div>
                      )}
                      {booking.price > 0 && (
                        <div className="flex items-center gap-2 text-xs text-green-600 font-bold">
                          <SafeIcon icon={FiDollarSign} className="w-3.5 h-3.5" />
                          <span>${parseFloat(booking.price).toFixed(2)}</span>
                        </div>
                      )}
                      {booking.notes && (
                        <div className="flex items-start gap-2 text-xs text-text-muted bg-white/60 p-2 rounded-xl">
                          <SafeIcon icon={FiInfo} className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                          <span className="line-clamp-2">{booking.notes}</span>
                        </div>
                      )}

                      {/* Actions */}
                      {(booking.status === 'pending_merchant' || booking.status === 'pending') && onUpdateStatus && (
                        <div className="flex gap-2 pt-1">
                          <button
                            onClick={() => onUpdateStatus(booking.id, 'rejected')}
                            className="flex-1 py-2 bg-white border border-red-200 text-red-600 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-red-50 transition-all"
                          >
                            Reject
                          </button>
                          <button
                            onClick={() => onUpdateStatus(booking.id, 'confirmed')}
                            className="flex-1 py-2 bg-green-500 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-green-600 transition-all shadow-sm"
                          >
                            Confirm
                          </button>
                        </div>
                      )}
                      {booking.status === 'confirmed' && onUpdateStatus && (
                        <div className="flex gap-2 pt-1">
                          <button
                            onClick={() => onUpdateStatus(booking.id, 'completed')}
                            className="flex-1 py-2 bg-purple-500 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-purple-600 transition-all shadow-sm"
                          >
                            Mark Complete
                          </button>
                          <button
                            onClick={() => onUpdateStatus(booking.id, 'cancelled')}
                            className="flex-1 py-2 bg-white border border-gray-200 text-gray-500 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-gray-50 transition-all"
                          >
                            Cancel
                          </button>
                        </div>
                      )}
                    </motion.div>
                  );
                })
              ) : selectedDate ? (
                <div className="flex flex-col items-center justify-center py-12 text-text-muted">
                  <SafeIcon icon={FiSun} className="w-10 h-10 mb-3 opacity-40" />
                  <p className="text-sm font-light">No bookings for this day.</p>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-text-muted">
                  <SafeIcon icon={FiCalendar} className="w-10 h-10 mb-3 opacity-40" />
                  <p className="text-sm font-light">Click a date to view bookings.</p>
                </div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingCalendar;